import React from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { LoadingPlaceholder, theme, VStack } from '@sg/garnish';

import { useTrackEffect } from '@order/Telemetry';

import { CreditMenuItem } from './CreditMenuItem';

// ─────────────────────────────────────────────────────────────────────────────

export const CreditMenu = (props: CreditMenuProps) => {
  const {
    creditAmount,
    isLoadingCredits,
    onPressAddPromoCode,
    onPressAvailableCredit,
  } = props;

  const { formatMessage, formatNumber } = useIntl();

  const addPromoCodeA11yLabel = formatMessage(
    messages.accountCreditPromoCodeA11yLabel,
  );
  const addPromoCodeLabel = formatMessage(
    messages.accountCreditFieldAddPromoCodePlaceholder,
  );
  // ─── Effects ─────────────────────────────────────────────────────────

  useTrackEffect('payment_credit.view_credit');

  // ─────────────────────────────────────────────────────────────────────

  return (
    <VStack gap={0} hasDivider={true} dividerColor={theme.colors.LIGHT}>
      {isLoadingCredits ? (
        <LoadingPlaceholder rows={1} rowHeight={100} palette="darkGray" />
      ) : (
        <CreditMenuItem
          testID="account.available-credit-details-button"
          iconName="IconCredit"
          title={formatMessage(messages.accountCreditMobileLabel)}
          accessibilityLabel={formatMessage(messages.accountCreditLabel)}
          subtitle={formatNumber(creditAmount / 100, {
            style: 'currency',
            currency: 'USD',
          })}
          onPress={onPressAvailableCredit}
        />
      )}

      <CreditMenuItem
        testID="account.open-promo-code-selection-button"
        iconName="IconPromo"
        title={addPromoCodeLabel}
        accessibilityLabel={addPromoCodeA11yLabel}
        onPress={onPressAddPromoCode}
      />
    </VStack>
  );
};

// ─── Types ───────────────────────────────────────────────────────────────────

export type CreditMenuProps = Readonly<{
  creditAmount: number;
  isLoadingCredits?: boolean;
  onPressAvailableCredit: () => void;
  onPressAddPromoCode: () => void;
}>;

const messages = defineMessages({
  accountCreditPromoCodeA11yLabel: {
    defaultMessage: 'Promo code',
    description: 'Account | Credit | Promo Code A11y Label',
  },
  accountCreditFieldAddPromoCodePlaceholder: {
    defaultMessage: 'Add promo code',
    description: 'Account | Credit | Field Add Promo Code Placeholder',
  },
  accountCreditMobileLabel: {
    defaultMessage: 'Available credit',
    description: 'Account | Credit | Mobile Label',
  },
  accountCreditLabel: {
    defaultMessage: 'Total credit',
    description: 'Account | Credit | Label',
  },
});
